import { useState, useEffect } from 'react';
import { repositoryFactory } from '@data/repositories/RepositoryFactory.js';

const asFiniteNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizePercent = (value) => {
  const numericValue = asFiniteNumber(value, 0);
  if (numericValue <= 1 && numericValue >= 0) {
    return clamp(numericValue * 100, 0, 100);
  }
  return clamp(numericValue, 0, 100);
};

const normalizeRatio = (value) => {
  const numericValue = asFiniteNumber(value, 0);
  if (numericValue > 1) {
    return clamp(numericValue / 100, 0, 1);
  }
  return clamp(numericValue, 0, 1);
};

const resolveLastActivity = (progressData) => {
  return (
    progressData?.lastActivityAt
    || progressData?.lastStudiedAt
    || progressData?.lastStudied
    || progressData?.lastAttemptAt
    || null
  );
};

const resolveAttempts = (questionProgress) => {
  const explicitAttempts = asFiniteNumber(
    questionProgress?.attempts ?? questionProgress?.timesShown,
    Number.NaN,
  );

  if (Number.isFinite(explicitAttempts)) {
    return Math.max(0, explicitAttempts);
  }

  const correctAttempts = asFiniteNumber(
    questionProgress?.correctAttempts ?? questionProgress?.timesCorrect,
    0,
  );
  const incorrectAttempts = asFiniteNumber(
    questionProgress?.incorrectAttempts ?? questionProgress?.timesIncorrect,
    0,
  );
  return Math.max(0, correctAttempts + incorrectAttempts);
};

const resolveCorrectAttempts = (questionProgress) => {
  return Math.max(
    0,
    asFiniteNumber(questionProgress?.correctAttempts ?? questionProgress?.timesCorrect, 0),
  );
};

const resolveAttemptHistory = (questionProgress) => {
  if (!Array.isArray(questionProgress?.history)) {
    return [];
  }

  return questionProgress.history.map((attempt) => {
    const timestampCandidate = attempt?.timestamp ?? attempt?.createdAt ?? null;
    const parsedTimestamp = timestampCandidate ? new Date(timestampCandidate) : null;
    
    // Try to parse selectedAnswer if it's JSON
    let selectedAnswer = attempt?.selectedAnswer ?? null;
    if (selectedAnswer && typeof selectedAnswer === 'string') {
      try {
        selectedAnswer = JSON.parse(selectedAnswer);
      } catch {
        // Keep as string if not JSON
      }
    }

    return {
      isCorrect: Boolean(attempt?.isCorrect ?? attempt?.correct),
      hintsUsed: Math.max(0, asFiniteNumber(attempt?.hintsUsed, 0)),
      timestamp:
        parsedTimestamp && !Number.isNaN(parsedTimestamp.getTime())
          ? parsedTimestamp.toISOString()
          : null,
      selectedAnswer,
      scoreAttempt: attempt?.scoreAttempt != null ? asFiniteNumber(attempt.scoreAttempt, null) : null,
      sessionHistoryId: attempt?.sessionHistoryId ?? null,
      skipped: Boolean(attempt?.skipped),
    };
  });
};

const resolveMostRecentTimestamp = (timestamps) => {
  const validTimestamps = (timestamps || [])
    .filter(Boolean)
    .map((timestamp) => new Date(timestamp))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime());

  return validTimestamps.length > 0 ? validTimestamps[0].toISOString() : null;
};

/**
 * Hook for managing and viewing user progress
 */
export function useUserProgress(userId, courseId) {
  const [progress, setProgress] = useState(null);
  const [moduleProgress, setModuleProgress] = useState([]);
  const [questionProgress, setQuestionProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const progressRepo = repositoryFactory.getProgressRepository();
  const courseRepo = repositoryFactory.getCourseRepository();

  /**
   * Load user progress data
   */
  const loadProgress = async () => {
    if (!userId || !courseId) {
      setProgress(null);
      setModuleProgress([]);
      setQuestionProgress([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get user's progress
      const userProgress = await progressRepo.getUserProgress(userId, courseId);
      setProgress(userProgress);

      if (userProgress) {
        // Get course structure to map progress
        const course = await courseRepo.getCourseById(courseId);
        
        if (course) {
          // Build question progress detail
          const questionDetails = [];
          course.sections.forEach((section) => {
            (section.questions || []).forEach((question) => {
              const qProg = userProgress.getQuestionProgress(question.id);
              if (qProg) {
                const attempts = resolveAttempts(qProg);
                const correctAttempts = resolveCorrectAttempts(qProg);
                const attemptHistory = resolveAttemptHistory(qProg);
                const rawMastery = qProg.masteryLevel
                  ?? qProg.mastery
                  ?? (attempts > 0 ? correctAttempts / attempts : 0);
                const questionScore = qProg.currentScore
                  ?? qProg.score
                  ?? (attempts > 0 ? (correctAttempts / attempts) * 100 : 0);
                const lastAttemptFromHistory = attemptHistory.length > 0
                  ? attemptHistory[attemptHistory.length - 1].timestamp
                  : null;

                questionDetails.push({
                  questionId: question.id,
                  moduleId: section.id,
                  moduleName: section.title,
                  questionText: question.text,
                  questionType: question.type,
                  attempts,
                  correctAttempts,
                  incorrectAttempts: Math.max(0, attempts - correctAttempts),
                  lastAttemptAt: qProg.lastAttemptAt || qProg.lastShown || lastAttemptFromHistory,
                  isCorrect: qProg.isCorrect ?? (attempts > 0 ? correctAttempts / attempts >= 0.5 : false),
                  score: normalizePercent(questionScore),
                  masteryLevel: normalizeRatio(rawMastery),
                  attemptHistory,
                  timesSkipped: Math.max(0, asFiniteNumber(qProg.timesSkipped, 0)),
                });
              }
            });
          });

          // Build module progress summary
          const moduleSummary = course.sections.map((section) => {
            const sectionProg = userProgress.getSectionProgress(section.id) || {};
            const moduleQuestions = questionDetails.filter((q) => q.moduleId === section.id);
            const attemptedFromSection = asFiniteNumber(
              sectionProg.questionsAttempted ?? sectionProg.completedQuestions,
              Number.NaN,
            );
            const completedQuestions = Number.isFinite(attemptedFromSection)
              ? Math.max(0, attemptedFromSection)
              : moduleQuestions.filter((q) => q.attempts > 0).length;
            const questionLastActivity = resolveMostRecentTimestamp(
              moduleQuestions.map((q) => q.lastAttemptAt),
            );
            const sectionScore = sectionProg.score
              ?? sectionProg.currentScore
              ?? sectionProg.masteryLevel
              ?? 0;

            return {
              moduleId: section.id,
              moduleName: section.title,
              isCompleted: userProgress.isSectionCompleted(section.id) || normalizePercent(sectionScore) >= 90,
              isUnlocked: userProgress.isSectionUnlocked(section.id) || sectionProg.unlocked === true,
              score: normalizePercent(sectionScore),
              totalQuestions: (section.questions || []).length,
              completedQuestions,
              lastActivity: resolveLastActivity(sectionProg) || questionLastActivity,
            };
          });

          setModuleProgress(moduleSummary);
          setQuestionProgress(questionDetails);
        } else {
          setModuleProgress([]);
          setQuestionProgress([]);
        }
      } else {
        setModuleProgress([]);
        setQuestionProgress([]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading user progress:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get overall statistics
   */
  const getStatistics = () => {
    if (!progress) {
      return {
        overallScore: 0,
        completedModules: 0,
        totalModules: 0,
        totalAttempts: 0,
        correctAttempts: 0,
      };
    }

    return {
      overallScore: normalizePercent(progress.overallScore),
      completedModules: moduleProgress.filter(m => m.isCompleted).length,
      totalModules: moduleProgress.length,
      totalAttempts: questionProgress.reduce((sum, q) => sum + asFiniteNumber(q.attempts, 0), 0),
      correctAttempts: questionProgress.reduce((sum, q) => sum + asFiniteNumber(q.correctAttempts, 0), 0),
    };
  };

  /**
   * Get progress by module
   */
  const getModuleProgress = (moduleId) => {
    return moduleProgress.find(m => m.moduleId === moduleId);
  };

  /**
   * Get questions for a specific module
   */
  const getModuleQuestions = (moduleId) => {
    return questionProgress.filter(q => q.moduleId === moduleId);
  };

  // Load progress when userId or courseId changes
  useEffect(() => {
    loadProgress();
  }, [userId, courseId]);

  return {
    progress,
    moduleProgress,
    questionProgress,
    loading,
    error,
    statistics: getStatistics(),
    getModuleProgress,
    getModuleQuestions,
    refresh: loadProgress,
  };
}
